/**
 * Bundle visibility state — the named axis a course bundle moves along as it goes
 * from committee editing to live-in-the-app (ra-gbrd.23).
 *
 * This is distinct from the editing scratchpad: the WIP draft
 * (events/{eventId}/wip/vNext) is where committee admins build courses. Once
 * published, a bundle is an immutable, *rendered* snapshot
 * (events/{eventId}/bundle/v{N}) that "looks like the real thing" — and it sits
 * in one of two pointer states on the event doc before/while it is public:
 *
 *   'draft'     — the WIP scratchpad, still being edited. Admin-only (the one
 *                 non-public piece). Not a published version, so it has no
 *                 v{N}; surfaced on the Course Cards page, not here.
 *   'preview'   — a published version pinned as the event's app-preview
 *                 (event.preview_content_version). A stable, rendered artifact a
 *                 reviewer (e.g. a race officer, who is a club admin — ra-gbrd.21)
 *                 can load in the app and sign off, BEFORE it goes live. The
 *                 preview pointer is public by design (ra-yuuv) so logged-out
 *                 staging testers can exercise it; review *access* is the
 *                 existing club-admin role, not a bespoke rule.
 *   'live'      — the active version (event.content_version): public / entitled
 *                 per the season access mode (free/paid/interest — ra-gbrd.5).
 *   'published' — a published version that is neither live nor the current
 *                 preview: it exists in history but is not pointed at.
 *
 * The flow the committee drives is: draft → (publish) → published → (set as app
 * preview) → preview → (activate) → live.
 */
export const BUNDLE_VISIBILITY = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  PREVIEW: 'preview',
  LIVE: 'live',
};

/** Display metadata per state. `hint` is the tooltip / longer description. */
export const BUNDLE_VISIBILITY_META = {
  [BUNDLE_VISIBILITY.DRAFT]: {
    label: 'Draft',
    hint: 'Work-in-progress in the editor — admin-only, not yet published',
  },
  [BUNDLE_VISIBILITY.PUBLISHED]: {
    label: 'Published',
    hint: 'Published version in history — not live, not the current preview',
  },
  [BUNDLE_VISIBILITY.PREVIEW]: {
    label: 'Preview',
    hint: 'Pinned as the app preview — admins (incl. the race officer) can review the rendered bundle before it goes live',
  },
  [BUNDLE_VISIBILITY.LIVE]: {
    label: 'Live',
    hint: 'The active version — live to users (per the season access mode)',
  },
};

/**
 * Derive the named visibility state for a single published bundle version.
 *
 * Precedence is live > preview > published: a version that is BOTH the active
 * and the preview pointer reads as 'live' (it is already public, so "preview"
 * adds nothing). 'draft' is never returned here — drafts are not published
 * versions; pass the WIP separately if you need to label it.
 *
 * @param {object} args
 * @param {number|null|undefined} args.version        - this version's number
 * @param {number|null|undefined} args.activeVersion  - event.content_version
 * @param {number|null|undefined} args.previewVersion - event.preview_content_version
 * @returns {'live'|'preview'|'published'}
 */
export function deriveBundleVisibility({ version, activeVersion, previewVersion } = {}) {
  if (version != null && activeVersion != null && version === activeVersion) {
    return BUNDLE_VISIBILITY.LIVE;
  }
  if (version != null && previewVersion != null && version === previewVersion) {
    return BUNDLE_VISIBILITY.PREVIEW;
  }
  return BUNDLE_VISIBILITY.PUBLISHED;
}

/** Convenience: the `{ state, label, hint }` triple for a published version. */
export function describeBundleVisibility(args) {
  const state = deriveBundleVisibility(args);
  return { state, ...BUNDLE_VISIBILITY_META[state] };
}
